import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Badge } from '../components/ui/Badge'
import { OrangeSection } from '../components/ui/OrangeSection'
import { 
  QrCode, 
  Download, 
  Copy, 
  Printer, 
  Settings,
  Plus,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  RefreshCw,
  Save,
  FileText,
  Image as ImageIcon,
  Link,
  Package,
  Hash,
  Calendar,
  User
} from 'lucide-react'

interface QRCodeTemplate {
  id: string
  name: string
  type: 'item' | 'location' | 'custom'
  content: string
  size: number
  format: 'png' | 'svg' | 'pdf'
  createdAt: string
  updatedAt: string
}

interface GeneratedQR {
  id: string
  content: string
  qrCodeData: string
  template: string
  size: number
  format: string
  createdAt: string
}

const mockTemplates: QRCodeTemplate[] = [
  {
    id: '1',
    name: 'Item QR Code',
    type: 'item',
    content: 'ITEM:{sku}|{ic}|{name}',
    size: 200,
    format: 'png',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z'
  },
  {
    id: '2',
    name: 'Location QR Code',
    type: 'location',
    content: 'LOC:{location}|{tote}',
    size: 150,
    format: 'svg',
    createdAt: '2024-01-14T09:00:00Z',
    updatedAt: '2024-01-14T09:00:00Z'
  },
  {
    id: '3',
    name: 'Custom URL',
    type: 'custom',
    content: 'https://bgs-core.com/item/{sku}',
    size: 250,
    format: 'png',
    createdAt: '2024-01-13T14:00:00Z',
    updatedAt: '2024-01-13T14:00:00Z'
  }
]

const mockGeneratedQRs: GeneratedQR[] = [
  {
    id: '1',
    content: 'ITEM:VLJ-2023-001|IC-001|Vintage Leather Jacket',
    qrCodeData: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==',
    template: 'Item QR Code',
    size: 200,
    format: 'png',
    createdAt: '2024-01-15T10:30:00Z'
  },
  {
    id: '2',
    content: 'LOC:Warehouse A|Tote 15',
    qrCodeData: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==',
    template: 'Location QR Code',
    size: 150,
    format: 'svg',
    createdAt: '2024-01-15T09:15:00Z'
  }
]

export default function QRGeneratorPage() {
  const [templates, setTemplates] = useState<QRCodeTemplate[]>(mockTemplates)
  const [generatedQRs, setGeneratedQRs] = useState<GeneratedQR[]>(mockGeneratedQRs)
  const [selectedTemplate, setSelectedTemplate] = useState<QRCodeTemplate | null>(null)
  const [customContent, setCustomContent] = useState('')
  const [qrSize, setQrSize] = useState(200)
  const [qrFormat, setQrFormat] = useState<'png' | 'svg' | 'pdf'>('png')
  const [showPreview, setShowPreview] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)

  const handleGenerateQR = async () => {
    if (!selectedTemplate && !customContent) return

    setIsGenerating(true)
    
    // Simulate QR generation
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    const newQR: GeneratedQR = {
      id: Date.now().toString(),
      content: customContent || selectedTemplate?.content || '',
      qrCodeData: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==',
      template: selectedTemplate?.name || 'Custom',
      size: qrSize,
      format: qrFormat,
      createdAt: new Date().toISOString()
    }

    setGeneratedQRs(prev => [newQR, ...prev])
    setIsGenerating(false)
  }

  const handleDownloadQR = (qr: GeneratedQR) => {
    const link = document.createElement('a')
    link.href = qr.qrCodeData
    link.download = `qr-code-${qr.id}.${qr.format}`
    link.click()
  }

  const handleCopyQR = (qr: GeneratedQR) => {
    navigator.clipboard.writeText(qr.content)
    // Show toast notification
    console.log('QR content copied to clipboard')
  }

  const handleDeleteQR = (qrId: string) => {
    setGeneratedQRs(prev => prev.filter(qr => qr.id !== qrId))
  }

  const getTypeIcon = (type: QRCodeTemplate['type']) => {
    const icons = {
      item: Package,
      location: Hash,
      custom: Link
    }
    const Icon = icons[type]
    return <Icon className="h-4 w-4" />
  }

  const getTypeColor = (type: QRCodeTemplate['type']) => {
    const colors = {
      item: 'text-blue-500',
      location: 'text-green-500',
      custom: 'text-purple-500'
    }
    return colors[type]
  }

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      <div className="space-y-6">
        {/* Header Section */}
        <div className="bg-black/40 backdrop-blur-sm border-2 border-orange-500/30 rounded-xl p-6 shadow-2xl shadow-orange-500/20">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/30 shadow-md">
                <QrCode className="h-7 w-7 text-orange-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-400">
                  QR Code Generator
                </h2>
                <p className="text-sm text-gray-400">
                  Generate QR codes for items, locations, and custom content with various templates.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* QR Generation Form */}
        <div className="bg-black/40 backdrop-blur-sm border-2 border-orange-500/30 rounded-xl p-6 shadow-2xl shadow-orange-500/20">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/30 shadow-md">
                <Settings className="h-7 w-7 text-orange-400" />
              </div>
              <h3 className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-400">
                Generate QR Code
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Template Selection */}
              <div>
                <label className="block text-sm text-white mb-2">Template</label>
                <div className="space-y-2">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-all duration-300 ${
                        selectedTemplate?.id === template.id
                          ? 'border-orange-500/40 bg-orange-500/10 shadow-[0_0_20px_rgba(251,146,60,0.2)]'
                          : 'border-orange-500/20 bg-gray-900/50 hover:bg-gray-800/50 hover:border-orange-400/30'
                      }`}
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={getTypeColor(template.type)}>
                          {getTypeIcon(template.type)}
                        </span>
                        <span className="text-white font-medium">{template.name}</span>
                        <Badge variant="secondary" className="text-xs">
                          {template.type}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-400">{template.content}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Content */}
              <div>
                <label className="block text-sm text-white mb-2">Custom Content</label>
                <textarea
                  value={customContent}
                  onChange={(e) => setCustomContent(e.target.value)}
                  placeholder="Enter custom content for QR code..."
                  className="w-full px-3 py-2 border border-orange-500/20 rounded-lg bg-gray-900/50 text-white focus:border-orange-400/40 focus:outline-none transition-colors"
                  rows={4}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Use variables like {'{sku}'}, {'{ic}'}, {'{name}'}, {'{location}'}, {'{tote}'}
                </p>
              </div>
            </div>

            {/* QR Settings */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-white mb-2">Size (px)</label>
                <Input
                  type="number"
                  value={qrSize}
                  onChange={(e) => setQrSize(parseInt(e.target.value))}
                  className="bg-gray-900/50 text-white border-orange-500/20 focus:border-orange-400/40"
                  min="50"
                  max="1000"
                />
              </div>
              <div>
                <label className="block text-sm text-white mb-2">Format</label>
                <select
                  value={qrFormat}
                  onChange={(e) => setQrFormat(e.target.value as 'png' | 'svg' | 'pdf')}
                  className="w-full px-3 py-2 border border-orange-500/20 rounded-lg bg-gray-900/50 text-white focus:border-orange-400/40 focus:outline-none transition-colors"
                >
                  <option value="png">PNG</option>
                  <option value="svg">SVG</option>
                  <option value="pdf">PDF</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleGenerateQR}
                  disabled={isGenerating || (!selectedTemplate && !customContent)}
                  className="w-full bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/30 text-white shadow-lg hover:shadow-xl hover:shadow-orange-500/20 transition-all duration-300"
                >
                  {isGenerating ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <QrCode className="h-4 w-4 mr-2" />
                  )}
                  {isGenerating ? 'Generating...' : 'Generate QR Code'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Generated QR Codes */}
        <div className="bg-black/40 backdrop-blur-sm border-2 border-orange-500/30 rounded-xl p-6 shadow-2xl shadow-orange-500/20">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/30 shadow-md">
                <ImageIcon className="h-7 w-7 text-orange-400" />
              </div>
              <h3 className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-400">
                Generated QR Codes
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {generatedQRs.map((qr) => (
                <div
                  key={qr.id}
                  className="group bg-gradient-to-br from-gray-900/95 via-orange-900/20 to-gray-900/95 backdrop-blur-md border border-orange-500/20 hover:border-orange-400/40 transition-all duration-300 hover:shadow-[0_0_30px_rgba(251,146,60,0.2)] hover:scale-[1.01] relative overflow-hidden rounded-xl"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 pointer-events-none bg-gradient-to-br from-transparent via-transparent to-orange-500/10" />
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-semibold text-orange-200">{qr.template}</h4>
                      <div className="flex items-center gap-1">
                        <Button
                          onClick={() => handleCopyQR(qr)}
                          size="sm"
                          variant="secondary"
                          className="hover:bg-orange-500/20 border-orange-500/30"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteQR(qr.id)}
                          size="sm"
                          variant="ghost"
                          className="hover:bg-red-500/20 hover:border-red-500/50"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="w-24 h-24 mx-auto mb-3 bg-white rounded border-2 border-dashed border-orange-400/50 flex items-center justify-center">
                        <QrCode className="h-8 w-8 text-orange-600" />
                      </div>
                      <p className="text-xs text-gray-400 mb-2 break-all">
                        {qr.content}
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                        <span className="font-medium">{qr.size}px</span>
                        <span className="text-orange-300">{qr.format.toUpperCase()}</span>
                        <span>{new Date(qr.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="flex gap-1 pt-3 border-t border-orange-500/20">
                        <Button
                          onClick={() => handleDownloadQR(qr)}
                          size="sm"
                          variant="secondary"
                          className="flex-1 hover:bg-orange-500/20 border-orange-500/30"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="hover:bg-orange-500/20 border-orange-500/30"
                        >
                          <Printer className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Template Management */}
        <div className="bg-black/40 backdrop-blur-sm border-2 border-orange-500/30 rounded-xl p-6 shadow-2xl shadow-orange-500/20">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/30 shadow-md">
                  <FileText className="h-7 w-7 text-orange-400" />
                </div>
                <h3 className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-400">
                  QR Templates
                </h3>
              </div>
              <Button
                size="sm"
                variant="secondary"
                className="bg-orange-500/20 hover:bg-orange-500/30 border-orange-500/30 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Template
              </Button>
            </div>
            <div className="space-y-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="flex items-center justify-between p-3 bg-gray-900/50 border border-orange-500/20 rounded-lg hover:bg-gray-800/50 hover:border-orange-400/30 transition-all duration-300"
                >
                  <div className="flex items-center gap-3">
                    <span className={getTypeColor(template.type)}>
                      {getTypeIcon(template.type)}
                    </span>
                    <div>
                      <p className="text-white font-medium">{template.name}</p>
                      <p className="text-sm text-gray-400">{template.content}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {template.size}px
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      {template.format.toUpperCase()}
                    </Badge>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="hover:bg-orange-500/20 border-orange-500/30"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="hover:bg-red-500/20 hover:border-red-500/50"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bulk Operations */}
        <div className="bg-black/40 backdrop-blur-sm border-2 border-orange-500/30 rounded-xl p-6 shadow-2xl shadow-orange-500/20">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/30 shadow-md">
                <Package className="h-7 w-7 text-orange-400" />
              </div>
              <h3 className="text-lg font-semibold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-400">
                Bulk Operations
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                variant="secondary"
                className="bg-orange-500/20 hover:bg-orange-500/30 border-orange-500/30 text-white shadow-lg hover:shadow-xl hover:shadow-orange-500/20 transition-all duration-300"
              >
                <Download className="h-4 w-4 mr-2" />
                Download All
              </Button>
              <Button
                variant="secondary"
                className="bg-orange-500/20 hover:bg-orange-500/30 border-orange-500/30 text-white shadow-lg hover:shadow-xl hover:shadow-orange-500/20 transition-all duration-300"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Batch
              </Button>
              <Button
                variant="secondary"
                className="bg-orange-500/20 hover:bg-orange-500/30 border-orange-500/30 text-white shadow-lg hover:shadow-xl hover:shadow-orange-500/20 transition-all duration-300"
              >
                <Save className="h-4 w-4 mr-2" />
                Save as Template
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
